import {
  CalendarClock, CheckCircle2, Circle, CircleDot, Clock, Loader2, LucideIcon, XCircle,
} from 'lucide-react';

/**
 * Single source of truth for job-status and subscription-tier presentation.
 * Replaces the maps previously duplicated (with drifting shapes) across
 * the old Dashboard, MatterDetail and SettingsPage.
 */

export type JobStatus = 'pending' | 'processing' | 'completed' | 'failed';

export interface StatusMeta {
  label: string;
  icon: LucideIcon;
  /** Icon/text color on white surfaces. */
  text: string;
  /** Tinted chip background. */
  bg: string;
  /** Status dot (Dashboard docket style). */
  dot: string;
}

export const STATUS_META: Record<JobStatus, StatusMeta> = {
  completed: {
    label: 'Completed',
    icon: CheckCircle2,
    text: 'text-emerald-600',
    bg: 'bg-emerald-50',
    dot: 'bg-emerald-500',
  },
  processing: {
    label: 'In progress',
    icon: Loader2,
    text: 'text-blue-600',
    bg: 'bg-blue-50',
    dot: 'bg-blue-500 animate-pulse',
  },
  pending: {
    label: 'Queued',
    icon: Clock,
    text: 'text-amber-600',
    bg: 'bg-amber-50',
    dot: 'bg-amber-400 animate-pulse',
  },
  failed: {
    label: 'Failed',
    icon: XCircle,
    text: 'text-red-500',
    bg: 'bg-red-50',
    dot: 'bg-red-500',
  },
};

export function statusMeta(status: string): StatusMeta {
  return STATUS_META[status as JobStatus] ?? STATUS_META.pending;
}

export interface TierMeta {
  label: string;
  /** Badge chip classes. */
  chip: string;
}

export const TIER_META: Record<string, TierMeta> = {
  free: { label: 'Free', chip: 'bg-legal-100 text-legal-700' },
  junior: { label: 'Junior', chip: 'bg-emerald-100 text-emerald-800' },
  pro: { label: 'Pro', chip: 'bg-accent text-legal-900' },
  firm: { label: 'Firm', chip: 'bg-blue-100 text-blue-800' },
  firm_pro: { label: 'Firm Pro', chip: 'bg-purple-100 text-purple-800' },
};

export function tierMeta(tier: string): TierMeta {
  return TIER_META[tier] ?? { label: tier, chip: TIER_META.free.chip };
}

// ---------------------------------------------------------------------------
// Practice meta (hearings / prep / tasks) — same shape as STATUS_META.
// ---------------------------------------------------------------------------

export type HearingStatus = 'scheduled' | 'held' | 'adjourned' | 'cancelled';

export const HEARING_STATUS_META: Record<HearingStatus, StatusMeta> = {
  scheduled: {
    label: 'Scheduled',
    icon: Clock,
    text: 'text-blue-600',
    bg: 'bg-blue-50',
    dot: 'bg-blue-500',
  },
  held: {
    label: 'Held',
    icon: CheckCircle2,
    text: 'text-emerald-600',
    bg: 'bg-emerald-50',
    dot: 'bg-emerald-500',
  },
  adjourned: {
    label: 'Adjourned',
    icon: CalendarClock,
    text: 'text-amber-600',
    bg: 'bg-amber-50',
    dot: 'bg-amber-400',
  },
  cancelled: {
    label: 'Cancelled',
    icon: XCircle,
    text: 'text-legal-400',
    bg: 'bg-legal-100',
    dot: 'bg-legal-300',
  },
};

export function hearingStatusMeta(status: string): StatusMeta {
  return HEARING_STATUS_META[status as HearingStatus] ?? HEARING_STATUS_META.scheduled;
}

export type PrepStatus = 'todo' | 'in_progress' | 'ready';

export const PREP_META: Record<PrepStatus, StatusMeta> = {
  todo: {
    label: 'Prep: to do',
    icon: Circle,
    text: 'text-legal-500',
    bg: 'bg-legal-100',
    dot: 'bg-legal-300',
  },
  in_progress: {
    label: 'Prep: in progress',
    icon: CircleDot,
    text: 'text-amber-600',
    bg: 'bg-amber-50',
    dot: 'bg-amber-400',
  },
  ready: {
    label: 'Prep: ready',
    icon: CheckCircle2,
    text: 'text-emerald-600',
    bg: 'bg-emerald-50',
    dot: 'bg-emerald-500',
  },
};

export function prepMeta(status: string): StatusMeta {
  return PREP_META[status as PrepStatus] ?? PREP_META.todo;
}

export type TaskStatus = 'open' | 'in_progress' | 'done' | 'cancelled';

export const TASK_STATUS_META: Record<TaskStatus, StatusMeta> = {
  open: {
    label: 'Open',
    icon: Circle,
    text: 'text-legal-600',
    bg: 'bg-legal-100',
    dot: 'bg-legal-400',
  },
  in_progress: {
    label: 'In progress',
    icon: CircleDot,
    text: 'text-blue-600',
    bg: 'bg-blue-50',
    dot: 'bg-blue-500',
  },
  done: {
    label: 'Done',
    icon: CheckCircle2,
    text: 'text-emerald-600',
    bg: 'bg-emerald-50',
    dot: 'bg-emerald-500',
  },
  cancelled: {
    label: 'Cancelled',
    icon: XCircle,
    text: 'text-legal-400',
    bg: 'bg-legal-100',
    dot: 'bg-legal-300',
  },
};

export function taskStatusMeta(status: string): StatusMeta {
  return TASK_STATUS_META[status as TaskStatus] ?? TASK_STATUS_META.open;
}
